import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Select, Space, Statistic, Table, Typography } from 'antd';
import { ingestionApi } from '../ingestion/ingestionApi';

const { Title, Text } = Typography;

const RANGE_OPTIONS = [
    { value: 'all', label: 'All time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This week' },
    { value: 'month', label: 'This month' },
];

// Start-of-window ISO for a preset (by item created_at); null = all time.
const rangeFrom = (preset) => {
    if (preset === 'all') return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (preset === 'week') d.setDate(d.getDate() - 6);
    else if (preset === 'month') d.setDate(d.getDate() - 29);
    return d.toISOString();
};

const red = { color: '#cf1322' };

const BY_TYPE_COLUMNS = [
    { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
    { title: 'Uploaded (visible)', dataIndex: 'published', key: 'published', align: 'right' },
    {
        title: 'In catalog, hidden',
        dataIndex: 'publishedHidden',
        key: 'publishedHidden',
        align: 'right',
        render: (v) => (v ? <span style={red}>{v}</span> : v),
    },
];

export const FullReport = () => {
    const [range, setRange] = useState('all');
    const [createdBy, setCreatedBy] = useState('');
    const [creators, setCreators] = useState([]);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        ingestionApi.getCreators().then(setCreators).catch(() => setCreators([]));
    }, []);

    useEffect(() => {
        setLoading(true);
        ingestionApi
            .getReport({ from: rangeFrom(range), createdBy })
            .then(setReport)
            .catch(() => setReport(null))
            .finally(() => setLoading(false));
    }, [range, createdBy]);

    const creatorOptions = useMemo(
        () => [
            { value: '', label: 'All creators' },
            ...creators.map((c) => ({ value: c.uid, label: c.email || c.uid })),
        ],
        [creators],
    );

    const totals = report?.totals || { published: 0, publishedHidden: 0, created: 0 };

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Upload report</Title>

            <Space wrap style={{ marginBottom: 16 }}>
                <Select style={{ width: 160 }} value={range} onChange={setRange} options={RANGE_OPTIONS} />
                <Select style={{ width: 240 }} value={createdBy} onChange={setCreatedBy} options={creatorOptions} />
            </Space>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Uploaded (visible)" value={totals.published} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" loading={loading}>
                        <Statistic title="In catalog, hidden" value={totals.publishedHidden} valueStyle={red} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card size="small" loading={loading}>
                        <Statistic title="Items created" value={totals.created} />
                    </Card>
                </Col>
            </Row>

            <Title level={4}>By type</Title>
            {(report?.byType || []).length === 0 && !loading ? (
                <Text type="secondary">No uploads in this range.</Text>
            ) : (
                <Table
                    rowKey="itemType"
                    loading={loading}
                    columns={BY_TYPE_COLUMNS}
                    dataSource={report?.byType || []}
                    pagination={false}
                    size="small"
                />
            )}
        </div>
    );
};
